import { useState, useEffect } from 'react'
import { X, Copy, Building2, AlertCircle } from 'lucide-react'
import { getApiBase, getToken } from '../lib/utils'
import { Workspace } from '../types/workspace'

interface DuplicateAgentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  agentId: number
  agentName: string
}

export default function DuplicateAgentModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  agentId, 
  agentName 
}: DuplicateAgentModalProps) {
  const [newName, setNewName] = useState(`${agentName} (copie)`)
  const [targetWorkspaceId, setTargetWorkspaceId] = useState<number | null>(null)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchWorkspaces()
      setNewName(`${agentName} (copie)`)
      setTargetWorkspaceId(null)
      setError(null)
    }
  }, [isOpen, agentName])

  const fetchWorkspaces = async () => {
    try {
      const token = getToken()
      if (!token) return

      const response = await fetch(`${getApiBase()}/workspaces?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        setWorkspaces(data.items || [])
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error)
    } finally {
      setLoadingWorkspaces(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const token = getToken()
      if (!token) {
        setError('Vous devez être connecté pour dupliquer un agent')
        setLoading(false)
        return
      }

      // Récupérer le workspace_id source actuel
      const currentWorkspaceId = localStorage.getItem('current_workspace_id')
      const wsParam = currentWorkspaceId ? `?workspace_id=${currentWorkspaceId}` : ''
      
      const response = await fetch(`${getApiBase()}/agents/${agentId}/duplicate${wsParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_name: newName,
          target_workspace_id: targetWorkspaceId,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Erreur lors de la duplication de l\'agent')
      }

      // Callback de succès
      if (onSuccess) {
        onSuccess()
      }
      
      // Fermer le modal
      onClose()
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setNewName(`${agentName} (copie)`)
      setTargetWorkspaceId(null)
      setError(null)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Copy className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-zinc-100">Dupliquer l'agent</h2>
                <p className="text-sm text-zinc-500">Créer une copie de "{agentName}"</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-400">Erreur</p>
                  <p className="text-sm text-red-300 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* New Name Input */}
            <div>
              <label htmlFor="new-name" className="block text-sm font-medium text-zinc-300 mb-2">
                Nom du nouvel agent
              </label>
              <input
                id="new-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: Mon agent (copie)"
                disabled={loading}
                required
                minLength={1}
                maxLength={255}
                className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Target Workspace Select */}
            <div>
              <label htmlFor="target-workspace" className="block text-sm font-medium text-zinc-300 mb-2">
                Workspace de destination
              </label>
              {loadingWorkspaces ? (
                <div className="px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-zinc-500 text-sm">
                  Chargement des workspaces...
                </div>
              ) : workspaces.length <= 1 ? (
                <div className="px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-zinc-500 text-sm">
                  Workspace actuel (vous n'avez qu'un seul workspace)
                </div>
              ) : (
                <select
                  id="target-workspace"
                  value={targetWorkspaceId || ''}
                  onChange={(e) => setTargetWorkspaceId(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Workspace actuel</option>
                  {workspaces.map((workspace) => (
                    <option key={workspace.id} value={workspace.id}>
                      {workspace.name}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-zinc-500 mt-2">
                {workspaces.length > 1 
                  ? "Sélectionnez un workspace ou laissez vide pour dupliquer dans le workspace actuel"
                  : "Plan Agency requis pour dupliquer vers un autre workspace"
                }
              </p>
            </div>

            {/* Info Box */}
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-sm text-blue-300">
                <strong>Éléments copiés :</strong> Configuration, instructions, modèle LLM, paramètres RAG, config MCP
              </p>
              <p className="text-xs text-blue-400 mt-2">
                Les associations avec les subjects et memory scopes seront copiées uniquement si elles existent dans le workspace cible.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-4 py-3 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || !newName.trim()}
                className="flex-1 px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Duplication...' : 'Dupliquer l\'agent'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
